import React from 'react';
import RefreshButton from '../../shared/RefreshButton';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import type { S3ObjectInfo } from '../../../../types/AWSTypes';

function formatBytes(bytes?: number): string {
    if (!bytes || bytes <= 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = bytes;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    const dp = i === 0 ? 0 : i === 1 ? 0 : 2;
    return `${v.toFixed(dp)} ${units[i]}`;
}

function keyBasename(key: string): string {
    const parts = key.split('/').filter(Boolean);
    return parts[parts.length - 1] || key;
}

type DeviceLogsTableProps = {
    kind: 'race' | 'debug';
    title: string;
    rows: S3ObjectInfo[];
    loading: boolean;
    error?: string;
    disabled?: boolean;
    onRefresh: () => void;
    onOpen: (row: S3ObjectInfo) => void;
    onDownload: (row: S3ObjectInfo) => void;
    /** Delete is admin-only (enforced server-side); omit to hide the action entirely. */
    onDelete?: (row: S3ObjectInfo) => void;
    deletingKey?: string | null;
};

export function DeviceLogsTable(props: DeviceLogsTableProps): React.JSX.Element {
    const {
        kind,
        title,
        rows,
        loading,
        error,
        disabled,
        onRefresh,
        onOpen,
        onDownload,
        onDelete,
        deletingKey,
    } = props;
    return (
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-sky-50 via-white to-white dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 flex items-center justify-between">
                <div className="text-sm font-semibold tracking-tight text-gray-900 dark:text-slate-100">{title}</div>
                <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                        {rows.length} file{rows.length === 1 ? '' : 's'}
                    </div>
                    <RefreshButton
                        busy={loading}
                        onClick={onRefresh}
                        disabled={disabled}
                        className="px-2.5 py-1 rounded-lg border text-[11px] bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-60"
                        busyText="Loading..."
                        busyClassName="inline-flex items-center gap-2 text-[11px] text-gray-600 dark:text-slate-300"
                    />
                </div>
            </div>

            <TableLoadingOverlay loading={loading} label={`Loading ${kind} logs...`}>
                {error ? (
                    <div className="p-4 text-sm text-red-600">Error: {error}</div>
                ) : rows.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500 dark:text-slate-400">No files.</div>
                ) : (
                    <div className="divide-y">
                        <div className="grid grid-cols-12 px-4 py-2 text-[11px] font-medium text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800">
                            <div className={onDelete ? 'col-span-5' : 'col-span-6'}>File</div>
                            <div className="col-span-2">Size</div>
                            <div className="col-span-3">Last Modified</div>
                            <div className={onDelete ? 'col-span-2 text-right' : 'col-span-1 text-right'}>Action</div>
                        </div>
                        <div className="max-h-[15.5rem] overflow-y-auto">
                            {rows.map((o) => {
                                const name = keyBasename(o.key);
                                const lm = o.lastModifiedEpochMs ? new Date(o.lastModifiedEpochMs).toLocaleString() : '-';
                                const isDeleting = deletingKey === o.key;
                                return (
                                    <div
                                        key={o.key}
                                        className="grid grid-cols-12 px-4 py-3 text-xs hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                                        onClick={() => onOpen(o)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className={`${onDelete ? 'col-span-5' : 'col-span-6'} min-w-0`}>
                                            <div className="font-medium text-gray-900 dark:text-slate-100 truncate">{name}</div>
                                            <div className="text-[11px] text-gray-500 dark:text-slate-400 truncate">{o.key}</div>
                                        </div>
                                        <div className="col-span-2 text-gray-700 dark:text-slate-300 self-center">{formatBytes(o.sizeBytes)}</div>
                                        <div className="col-span-3 text-gray-700 dark:text-slate-300 truncate self-center">{lm}</div>
                                        <div className={`${onDelete ? 'col-span-2' : 'col-span-1'} text-right self-center`}>
                                            <button
                                                type="button"
                                                className="text-xs text-blue-600 hover:underline"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDownload(o);
                                                }}
                                            >
                                                Download
                                            </button>
                                            {onDelete ? (
                                                <button
                                                    type="button"
                                                    className="ml-2 text-xs text-red-600 hover:underline disabled:opacity-50"
                                                    disabled={isDeleting}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDelete(o);
                                                    }}
                                                >
                                                    {isDeleting ? 'Deleting…' : 'Delete'}
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </TableLoadingOverlay>
        </div>
    );
}
